<script lang="ts">
  /**
   * A one-line search box: a magnifier, the input, and — once something is
   * typed — a clear button.
   *
   * The `label` is the input's accessible name and its placeholder both, so
   * the field says what it is for whether or not anything is in it. Enter
   * calls `onsubmit`. The value is bindable: the caller reads what was typed
   * and may clear it.
   *
   * The magnifier is decoration; the clear button is named in the shell's own
   * words (`shellText('clear')`).
   *
   * With `combobox` the input becomes the combobox of ARIA's pattern
   * (`SearchSuggest`, TwinTen ADR 0065): it names the listbox it controls, whether
   * that is showing, and which option is active — focus never leaves the
   * input. `onkeydown` sees every key first; a key it handles (calls
   * `preventDefault`) is not also an Enter-to-submit.
   */
  import { shellText } from '../i18n.svelte.js';

  interface Props {
    value?: string;
    readonly label: string;
    readonly onsubmit?: (() => void) | undefined;
    readonly maxlength?: number | undefined;
    readonly disabled?: boolean;
    readonly testid?: string | undefined;
    /** The combobox wiring, set by `SearchSuggest`. Absent: a plain search box. */
    readonly combobox?:
      | {
          readonly listId: string;
          readonly expanded: boolean;
          readonly activeId: string | undefined;
        }
      | undefined;
    readonly onkeydown?: ((event: KeyboardEvent) => void) | undefined;
  }
  let {
    value = $bindable(''),
    label,
    onsubmit,
    maxlength,
    disabled = false,
    testid,
    combobox,
    onkeydown,
  }: Props = $props();

  let input = $state<HTMLInputElement | null>(null);

  function onKeydown(event: KeyboardEvent): void {
    onkeydown?.(event);
    if (event.defaultPrevented || event.key !== 'Enter') return;
    event.preventDefault();
    onsubmit?.();
  }

  function clear(): void {
    value = '';
    input?.focus();
  }
</script>

<div class="ui-search" class:ui-search--disabled={disabled}>
  <svg class="ui-search-glyph" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="2" />
    <path
      d="M16 16l4.5 4.5"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
    />
  </svg>
  <input
    bind:this={input}
    bind:value
    class="ui-search-input"
    type="search"
    autocapitalize="off"
    autocomplete="off"
    spellcheck="false"
    placeholder={label}
    aria-label={label}
    role={combobox === undefined ? undefined : 'combobox'}
    aria-autocomplete={combobox === undefined ? undefined : 'list'}
    aria-controls={combobox?.listId}
    aria-expanded={combobox === undefined ? undefined : combobox.expanded}
    aria-activedescendant={combobox?.activeId}
    {maxlength}
    {disabled}
    data-testid={testid}
    onkeydown={onKeydown}
  />
  {#if value.length > 0 && !disabled}
    <button
      type="button"
      class="ui-search-clear"
      aria-label={shellText('clear')}
      data-testid="ui-search-clear"
      onclick={clear}
    >
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          d="M7 7l10 10M17 7 7 17"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>
  {/if}
</div>

<style>
  .ui-search {
    display: flex;
    align-items: center;
    gap: var(--ui-space-2);
    min-height: 2.9rem;
    padding: 0 var(--ui-space-2) 0 var(--ui-space-3);
    border: 1.5px solid var(--ui-ink-2);
    border-radius: 999px;
    /* The card ground, not --ui-surface-2: the placeholder is --ui-ink-2, which
       is gated as text on a card and on a sheet but not on the quieter fill. */
    background: var(--ui-card);
    color: var(--ui-ink-2);
    font-family: var(--ui-font);
  }

  .ui-search:focus-within {
    outline: 3px solid var(--ui-accent);
    outline-offset: 1px;
  }

  .ui-search--disabled {
    opacity: 0.6;
  }

  .ui-search-glyph {
    flex: 0 0 auto;
  }

  .ui-search-input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    padding: 0.6rem 0;
    background: transparent;
    color: var(--ui-ink);
    font-family: var(--ui-font);
    font: var(--ui-text-body);
    font-weight: 700;
    -webkit-appearance: none;
    appearance: none;
  }

  .ui-search-input::placeholder {
    color: var(--ui-ink-2);
    opacity: 1;
  }

  /* The browser's own clear cross would sit beside ours. */
  .ui-search-input::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }

  .ui-search-clear {
    flex: 0 0 auto;
    width: 2.25rem;
    height: 2.25rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--ui-ink);
  }
</style>
